import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SellerShippingRuleView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateSellerShippingRules } from "@/lib/query/core/invalidation";

type CreateShippingRuleInput = {
  storeSlug: string;
  name?: string | null;
  estimatedDeliveryDays: number;
  price: number;
  governorateIds: string[];
};

type UpdateShippingRuleInput = {
  storeSlug: string;
  ruleId: string;
  name?: string | null;
  estimatedDeliveryDays?: number;
  price?: number;
  governorateIds?: string[];
  isActive?: boolean;
};

type DeleteShippingRuleInput = {
  storeSlug: string;
  ruleId: string;
};

export function useCreateShippingRuleMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: CreateShippingRuleInput) => {
      const { storeSlug: _storeSlug, ...body } = input;
      void _storeSlug;
      return apiJsonClient<SellerShippingRuleView>(endpoints.seller.storeShippingRules, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    onSuccess: async (_data, variables) => {
      await invalidateSellerShippingRules(queryClient, variables.storeSlug);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export function useUpdateShippingRuleMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: UpdateShippingRuleInput) => {
      const { storeSlug: _storeSlug, ruleId, ...body } = input;
      void _storeSlug;
      return apiJsonClient<SellerShippingRuleView>(
        endpoints.seller.storeShippingRuleById(ruleId),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
    },
    onSuccess: async (_data, variables) => {
      await invalidateSellerShippingRules(queryClient, variables.storeSlug);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export function useDeleteShippingRuleMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: DeleteShippingRuleInput) => {
      return apiJsonClient<void>(
        endpoints.seller.storeShippingRuleById(input.ruleId),
        {
          method: "DELETE",
        },
      );
    },
    onSuccess: async (_data, variables) => {
      await invalidateSellerShippingRules(queryClient, variables.storeSlug);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
